import { Component, EventListener, getComponentByDomNode, startComponents } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { getCurrentViewport, VIEWPORT_ENTERED } from '@gondel/plugin-media-queries';
import { Header } from '../../../organisms/header/js/header';
import ajax from '@swisslife/shared-ui/src/utils/ajax/js/ajax';
// make sure NavigationListRenderer is included
import '../react/NavigationListRenderer';
import { ILinkItem } from '@swisslife/shared-types/src/aem/items/LinkItem';
import { dispatchCustomEvent, EventNames } from '@swisslife/shared-ui/src/utils/custom-event/js/custom-event';

export interface INavigationListI18n {
	loading: string;
	backButton: string;
}

enum Selectors {
	NavigationOverlayLarge = '.js-m-main-navigation__overlay-large',
	ReactOutlet = '.js-m-main-navigation__list-outlet',
	ReactRenderer = '.js-m-main-navigation__list-renderer',
	Loader = '.js-m-main-navigation__loader',
	Item = '.js-m-main-navigation__list-item',
	TopLevelItem = '.js-m-main-navigation__list-top-level-item',
	TopLevelItemLink = '.js-m-main-navigation__list-top-level-item-link',
	SubLevelLink = '.js-m-main-navigation__list-sub-level-link',
	BackButton = '.js-m-main-navigation__button-back',
	GeneralError = '.js-m-main-navigation__error',
}

enum States {
	Loading = 'state-m-main-navigation--loading',
	TopLevelActive = 'state-m-main-navigation--top-level-active',
	TopLevelHover = 'state-m-main-navigation--top-level-hover',
	LinkActive = 'state-a-link--active',
	ItemActive = 'state-m-main-navigation--active',
	ItemTransition = 'state-m-main-navigation--transition',
	ItemCurrentPage = 'state-m-main-navigation--current-page',
	HasChildren = 'state-m-main-navigation--has-children',
}

enum DataEntry {
	ItemUrl = 'item-url',
}

interface INavigationLink {
	text: string;
	href: string;
	path: string;
	openInNewWindow: boolean;
}

interface INavigationItem {
	link: INavigationLink;
	items: INavigationItem[];
}

interface INavigationEntryResult {
	items: INavigationItem[];
}

interface IResultsCache {
	[field: string]: INavigationEntryResult;
}

@Component('MainNavigation')
export class MainNavigation extends GondelJqueryComponent {
	private $header: JQuery;
	private $navigationOverlay: JQuery;
	private $topLevelItemLinks: JQuery;
	private header: Header;
	private uxBehaviourType;
	private resultsCache: IResultsCache = {};
	private i18n: INavigationListI18n;
	private menuHasLoaded: boolean = false;

	public start() {
		this.$header = this.$ctx.closest('.o-header');
		this.$navigationOverlay = $(Selectors.NavigationOverlayLarge, this.$ctx);
		this.$topLevelItemLinks = $(Selectors.TopLevelItemLink, this.$ctx);

		const currentVp = getCurrentViewport();
		this.updateUxBehaviourType(currentVp);

		this.i18n = {
			loading: this.$ctx.data('i18n-loading'),
			backButton: this.$ctx.data('i18n-back-button'),
		};

		this.setupViewportDependingBehaviour();

		if (this.$header.length === 0) {
			// tslint:disable-next-line
			return console.warn(`Use MainNavigation just within a header!`);
		} else {
			this.header = getComponentByDomNode<Header>(this.$header);
		}
	}

	/**
	 * Reset the active state class on all links
	 */
	public resetItemsActiveState(withAnimation: boolean) {
		$(Selectors.TopLevelItemLink, this.$ctx).removeClass(States.LinkActive);
		const $item = $(Selectors.Item, this.$ctx);

		if (withAnimation) {
			// remove transition class to trigger slideUp transition on the navigation overlay
			$item.removeClass(States.ItemTransition);

			const $overlay = $(
				`${Selectors.TopLevelItem}.${States.ItemActive} ${Selectors.NavigationOverlayLarge}`,
				this.$ctx
			);

			if ($overlay.length > 0) {
				$overlay.one('transitionend', () => {
					// remove active class to set navigation overlay to display none again
					$item.removeClass(States.ItemActive);
					this.$ctx.removeClass(States.TopLevelActive);

					// delete previously rendered instances
					$(Selectors.ReactRenderer, this.$ctx).remove();
				});
			}
		} else {
			$item.removeClass(States.ItemActive);
			this.$ctx.removeClass(States.TopLevelActive);

			// delete previously rendered instances
			$(Selectors.ReactRenderer, this.$ctx).remove();
		}
	}

	/**
	 * Handle the event when the user clicks on the back button
	 * @param ev {Event}		click event
	 */
	@EventListener('click', Selectors.BackButton)
	public _handleBackButtonLinkClick(ev: MouseEvent) {
		ev.preventDefault();

		const $target = $(ev.currentTarget!);

		if (this.uxBehaviourType === 'small' && $target.attr('data-current-level') === '0') {
			this.resetItemsActiveState(false);
		}
	}

	/**
	 * Handle the event when the user clicks on a top level navigation link
	 * @param ev {Event}		click event
	 */
	@EventListener('click', Selectors.TopLevelItemLink)
	public _handleTopLevelItemLinkClick(
		ev: JQuery.ClickEvent<HTMLDivElement, any, HTMLAnchorElement | HTMLButtonElement>
	) {
		if (
			!$(ev.target!)
				.closest(Selectors.TopLevelItem)
				.hasClass(States.HasChildren)
		) {
			return;
		}

		ev.preventDefault();

		if (!ev.target) {
			return;
		}

		const $link = $(ev.currentTarget!);
		const $item = $link.closest(Selectors.Item);

		this.handleTopLevelClassStates($item, $link);

		if ($link.hasClass(States.LinkActive)) {
			this.prepareSubNavigationForEntry($item);
		}
	}

	/**
	 * Handle the event when the user clicks on a sub level navigation link
	 * @param ev {Event}		click event
	 */
	@EventListener('click', Selectors.SubLevelLink)
	public _handleSubLevelLinkClick(ev: MouseEvent) {
		const $link = $(ev.currentTarget!);
		const $item = $link.closest(Selectors.Item);

		if ($item.hasClass(States.HasChildren)) {
			ev.preventDefault();

			if (this.uxBehaviourType === 'small') {
				this.header.scrollOverlaySmallToTop();
			} else {
				this.$navigationOverlay.animate(
					{
						scrollTop: 0,
					},
					250
				);
			}
			return false;
		} else {
			return true;
		}
	}

	@EventListener(VIEWPORT_ENTERED)
	public _handleViewportChangeEvent(event) {
		const previousUxBehaviourType = this.uxBehaviourType;
		this.updateUxBehaviourType(event.viewport);

		// if the viewport change was from small to medium or vice versa, we reset the item active state
		if (previousUxBehaviourType !== this.uxBehaviourType) {
			this.resetItemsActiveState(false);
			this.setupViewportDependingBehaviour();
		}
	}

	/**
	 * Setup the viewport depending behaviour
	 */
	private setupViewportDependingBehaviour() {
		if (this.uxBehaviourType === 'small') {
			this.checkInitialActiveState();
			this.removeHoverBehaviour();
		} else {
			this.addHoverBehaviour();
		}
	}

	/**
	 * Add the listeners needed for the hover behaviour
	 */
	private addHoverBehaviour() {
		// hover classes handled in JS because the focused element
		// gets black and the other elements will grey-out.
		this.$topLevelItemLinks.on('mouseover', () => {
			this.$ctx.addClass(States.TopLevelHover);
		});

		this.$topLevelItemLinks.on('mouseout', () => {
			this.$ctx.removeClass(States.TopLevelHover);
		});
	}

	/**
	 * Remove the listeners needed for the hover behaviour
	 */
	private removeHoverBehaviour() {
		this.$topLevelItemLinks.off('mouseover');
		this.$topLevelItemLinks.off('mouseout');
	}

	/**
	 * Handle the top level classes
	 * @param $item {Object}		the jQuery item element
	 * @param $link {Object}		the jQuery link element
	 */
	private handleTopLevelClassStates($item: JQuery, $link: JQuery) {
		// reset possibly previous active item
		if (!$link.hasClass(States.LinkActive)) {
			this.resetItemsActiveState(false);
		}

		// toggle link active class
		$link.toggleClass(States.LinkActive);

		const $navigationOverlay = $(Selectors.NavigationOverlayLarge, $item);

		if ($link.hasClass(States.LinkActive)) {
			// add item active class to set display block on the navigation overlay
			$item.addClass(States.ItemActive);
			// Reflow to make transition work
			$item.outerWidth();
			// add transition class to trigger slideDown transition on the navigation overlay
			$item.addClass(States.ItemTransition);

			this.$ctx.addClass(States.TopLevelActive);

			$navigationOverlay.one('transitionend', () => {
				this.header.toggleFocusTrap(true);
			});
		} else {
			this.$ctx.removeClass(States.TopLevelActive);

			// remove transition class to trigger slideUp transition on the navigation overlay
			$item.removeClass(States.ItemTransition);

			$navigationOverlay.one('transitionend', () => {
				// remove active class to set navigation overlay to display none again
				$item.removeClass(States.ItemActive);
			});
		}

		// set class on the header depending on if the link is active or not
		this.header.toggleNavigationOverlayStateClass($link.hasClass(States.LinkActive));
	}

	/**
	 * Check if we have any initial active items to set correct state for uxBehaviourType small
	 */
	private checkInitialActiveState() {
		const $activeItems = this.$ctx.find(`${Selectors.TopLevelItem}.${States.ItemCurrentPage}`);

		if ($activeItems.length === 1) {
			$activeItems.addClass(States.ItemActive);
			this.$ctx.addClass(States.TopLevelActive);
			this.prepareSubNavigationForEntry($activeItems);
		}
	}

	/**
	 * Load the sub level data for the provided element
	 * @param $item {Object}		the jQuery item element
	 */
	private async prepareSubNavigationForEntry($item: JQuery) {
		const $link = $(Selectors.TopLevelItemLink, $item);
		const hasChildren = $link.parent(Selectors.TopLevelItem).hasClass(States.HasChildren);

		// https://jira.namics.com/browse/SLEVO-2338
		// go back to main menu in case of main-menu element is lastElement
		if (!hasChildren) {
			this.resetItemsActiveState(false);
		}

		const result = await this.getDataForEntry($item);
		const $itemReactOutlet = $(Selectors.ReactOutlet, $item);

		if (!result || !result.items) {
			const $generalError = this.$ctx
				.find(Selectors.GeneralError)
				.children()
				.clone();

			$itemReactOutlet.html('').append($generalError);
			return;
		}

		// https://jira.namics.com/browse/SLEVO-2337
		// go back to main menu in case of there are no child elements
		if (result.items.length === 0) {
			this.resetItemsActiveState(false);
		}

		const href = ($link.attr('href') || '').trim();
		const selectedFirstLevelLink: ILinkItem = {
			text: $link.text().trim() || '',
			href,
			path: href.split('?')[0].split('.')[0],
			openInNewWindow: false,
		};

		const data = {
			result,
			i18n: this.i18n,
			selectedFirstLevelLink,
			shouldRenderCurrentPage: !(this.menuHasLoaded && this.uxBehaviourType === 'small'),
			currentPath: this.$ctx.data('current-path'),
		};

		const $childRenderer = $(
			`<div
					data-g-name="NavigationListRenderer"
					role="menubar"
					class="m-main-navigation__list-renderer js-m-main-navigation__list-renderer"
				>
				<script type="text/json">${JSON.stringify(data)}</script>
				${this.i18n.loading}
			</div>`
		);

		$itemReactOutlet.html('').append($childRenderer);

		this.$navigationOverlay.removeClass(States.Loading);
		this.menuHasLoaded = true;

		startComponents($item);
	}

	/**
	 * Get the navigation data for the provided item
	 * @param $item {object}		the lvl 1 item to get the navigation data for
	 */
	private async getDataForEntry($item: JQuery): Promise<INavigationEntryResult> {
		const itemUrl = $item.data(DataEntry.ItemUrl);
		let result: INavigationEntryResult = {
			items: [],
		};

		if (this.resultsCache.hasOwnProperty(itemUrl)) {
			return this.resultsCache[itemUrl];
		}

		try {
			result = await ajax<INavigationEntryResult>(itemUrl, {
				method: 'GET',
				type: 'text/json',
			});

			if (typeof result === 'string') {
				result = JSON.parse(result);
			}

			this.resultsCache[itemUrl] = result;
		} catch (error) {
			// network error happened (error handling happens in prepareSubNavigationForEntry())
			const statusCode = error.status ? error.status.toString() : 'n/a';
			dispatchCustomEvent(this.$ctx, EventNames.NetworkError, { statusCode });
		}

		return result;
	}

	/**
	 * Update the uxBehaviourType variable
	 * @param viewport {string}		the new viewport
	 */
	private updateUxBehaviourType(viewport: string) {
		if (viewport === 'large' || viewport === 'xlarge' || viewport === 'xxlarge') {
			this.uxBehaviourType = 'large';
		} else {
			this.uxBehaviourType = 'small';
		}
	}
}
